import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { copyTextReliable } from "@/lib/clipboard";
import {
  Newspaper, RefreshCw, Image as ImageIcon, Copy, Check, Link2, Eye, EyeOff,
  ExternalLink, Loader2, CalendarDays, Layers,
} from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function AdminRevuePresse() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState({});
  const [editing, setEditing] = useState({});
  const [copied, setCopied] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/admin/revue-presse").then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const runToday = async () => {
    setRunning(true);
    try {
      await api.post("/admin/revue-presse/run");
      toast.success("Génération lancée. La revue du jour sera prête dans ~1 à 2 minutes — actualisez ensuite.");
    } catch (e) { toast.error("Impossible de lancer la génération."); }
    finally { setRunning(false); }
  };

  const setBusyFor = (slug, v) => setBusy((b) => ({ ...b, [slug]: v }));

  const regenImage = async (slug) => {
    setBusyFor(slug, "img");
    try {
      const { data } = await api.post(`/admin/revue-presse/${slug}/regenerate-image`);
      if (data.cover_image) { toast.success("Nouvelle image générée."); load(); }
      else toast.error("Image non générée, réessayez.");
    } catch (e) { toast.error("Échec de la génération d'image."); }
    finally { setBusyFor(slug, null); }
  };

  const toggleStatus = async (p) => {
    const next = p.status === "published" ? "draft" : "published";
    setBusyFor(p.slug, "status");
    try {
      await api.patch(`/admin/revue-presse/${p.slug}/status`, { status: next });
      toast.success(next === "published" ? "Revue publiée." : "Revue dépubliée.");
      load();
    } catch (e) { toast.error("Action impossible."); }
    finally { setBusyFor(p.slug, null); }
  };

  const saveShareText = async (slug) => {
    setBusyFor(slug, "save");
    try {
      await api.patch(`/admin/revue-presse/${slug}/share-text`, { share_text: editing[slug] });
      toast.success("Texte de partage enregistré.");
      setEditing((e) => { const n = { ...e }; delete n[slug]; return n; });
      load();
    } catch (e) { toast.error("Enregistrement impossible."); }
    finally { setBusyFor(slug, null); }
  };

  const copy = (key, text) => {
    if (copyTextReliable(text)) { setCopied(key); setTimeout(() => setCopied(""), 2000); toast.success("Copié !"); }
    else toast.error("Copie impossible.");
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-revue-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900 flex items-center gap-2"><Layers size={22} className="text-[#E05638]" /> Revues de presse quotidiennes</h1>
          <p className="mt-1 text-sm text-slate-500 max-w-xl">La revue du jour est générée automatiquement après la production des actualités (cron du matin). Vous pouvez la (re)générer, régénérer son image, modifier son texte de partage et la publier/dépublier.</p>
        </div>
        <button onClick={runToday} disabled={running} data-testid="admin-revue-run"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#E05638] text-white font-bold text-sm hover:bg-[#c94a2f] disabled:opacity-50 transition-colors">
          {running ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Générer / mettre à jour la revue du jour
        </button>
      </div>

      {loading ? (
        <div className="mt-10 text-slate-500">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="mt-10 text-center py-16 text-slate-500 border border-dashed border-slate-200 rounded-2xl" data-testid="admin-revue-empty">
          Aucune revue générée pour le moment. Cliquez sur « Générer la revue du jour » (des actualités doivent avoir été publiées aujourd'hui).
        </div>
      ) : (
        <div className="mt-8 space-y-5">
          {items.map((p) => {
            const edited = editing[p.slug] !== undefined;
            const shareVal = edited ? editing[p.slug] : (p.share_text || "");
            const appUrl = `/revue-presse/${p.slug}`;
            const vitrineUrl = `${BASE}/api/revue-vitrine/${p.slug}`;
            return (
              <div key={p.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`admin-revue-card-${p.slug}`}>
                <div className="flex flex-col sm:flex-row">
                  <div className="sm:w-56 shrink-0 bg-slate-100 aspect-[1.91/1] overflow-hidden">
                    {p.cover_image ? <img src={p.cover_image} alt="" className="w-full h-full object-cover" data-testid={`admin-revue-cover-${p.slug}`} />
                      : <div className="w-full h-full grid place-items-center text-slate-300"><Newspaper size={32} /></div>}
                  </div>
                  <div className="flex-1 min-w-0 p-4 sm:p-5">
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                      <span className={`px-2 py-0.5 rounded-full font-bold ${p.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid={`admin-revue-status-${p.slug}`}>
                        {p.status === "published" ? "Publiée" : "Brouillon"}
                      </span>
                      {p.digest_date && <span className="inline-flex items-center gap-1 text-slate-500"><CalendarDays size={12} /> {p.digest_date}</span>}
                      {p.article_count > 0 && <span className="inline-flex items-center gap-1 text-slate-500"><Newspaper size={12} /> {p.article_count} articles</span>}
                      <span className="inline-flex items-center gap-1 text-slate-400"><Eye size={12} /> {p.views || 0}</span>
                    </div>
                    <h2 className="mt-2 font-heading text-lg font-bold text-slate-900">{p.title}</h2>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <a href={appUrl} target="_blank" rel="noreferrer" data-testid={`admin-revue-view-${p.slug}`}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50"><ExternalLink size={14} /> Aperçu</a>
                      <button onClick={() => regenImage(p.slug)} disabled={!!busy[p.slug]} data-testid={`admin-revue-regen-image-${p.slug}`}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                        {busy[p.slug] === "img" ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />} Régénérer l'image
                      </button>
                      <button onClick={() => toggleStatus(p)} disabled={!!busy[p.slug]} data-testid={`admin-revue-toggle-${p.slug}`}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                        {busy[p.slug] === "status" ? <Loader2 size={14} className="animate-spin" /> : (p.status === "published" ? <EyeOff size={14} /> : <Eye size={14} />)}
                        {p.status === "published" ? "Dépublier" : "Publier"}
                      </button>
                      <button onClick={() => copy(`link-${p.slug}`, vitrineUrl)} data-testid={`admin-revue-copy-link-${p.slug}`}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                        {copied === `link-${p.slug}` ? <Check size={14} /> : <Link2 size={14} />} Copier le lien
                      </button>
                    </div>

                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Texte de partage</span>
                        <button onClick={() => copy(`txt-${p.slug}`, shareVal)} data-testid={`admin-revue-copy-text-${p.slug}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-[#E05638] hover:underline">
                          {copied === `txt-${p.slug}` ? <Check size={13} /> : <Copy size={13} />} Copier
                        </button>
                      </div>
                      <textarea data-testid={`admin-revue-sharetext-${p.slug}`} rows={6} value={shareVal}
                        onChange={(e) => setEditing((s) => ({ ...s, [p.slug]: e.target.value }))}
                        className="w-full text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-xl p-3 resize-y focus:outline-none focus:ring-2 focus:ring-[#E05638]/40" />
                      {edited && (
                        <button onClick={() => saveShareText(p.slug)} disabled={busy[p.slug] === "save"} data-testid={`admin-revue-save-text-${p.slug}`}
                          className="mt-2 inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#172A3A] text-white text-sm font-semibold hover:bg-[#223c52] disabled:opacity-50">
                          {busy[p.slug] === "save" ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Enregistrer le texte
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
